import type {ReactNode} from "react";
import {useEffect, useState} from "react";
import {useConnectionState} from "../../models/use-evergreen-sync";
import {Box, Col, Text} from "../../ui/Box";
import Icon from "../../ui/Icon";
import {iconStyles} from "../../ui/Icon.css";

const getTime = (date: Date | null) => {
  if (!date) return null;
  return Math.floor(date.getTime() / 1000);
};

const getNextTickSecs = (secs: number) => {
  if (secs < 60) return 10;
  return 60;
};

const getNowSecs = () => Math.floor(new Date().getTime() / 1000);

const getDiffLabel = (secs: number) => {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h`;
};

const Time = ({lastSyncedSecs}: {lastSyncedSecs: number}) => {
  const [diffSecs, setDiffSecs] = useState(() => getNowSecs() - lastSyncedSecs);
  useEffect(() => {
    const nextTickS = getNextTickSecs(diffSecs);
    const timeoutId = setTimeout(
      () => setDiffSecs(getNowSecs() - lastSyncedSecs),
      nextTickS * 1000
    );
    return () => clearTimeout(timeoutId);
  }, [diffSecs, lastSyncedSecs]);
  if (diffSecs < 10) return null;
  return (
    <Pill>
      <Text size="10">{getDiffLabel(diffSecs)}</Text>
    </Pill>
  );
};

// shows a nonzero status right away, and keeps showing it for at least 500ms after it appeared
const useLaggingStatus = (val: number): number => {
  const [held, setHeld] = useState(() => (val !== 0 ? {val} : null));
  const [prevVal, setPrevVal] = useState(val);
  if (val !== prevVal) {
    setPrevVal(val);
    // a new object, so the timer restarts even when the same status comes back
    if (val !== 0) setHeld({val});
  }
  useEffect(() => {
    if (!held) return;
    const timeoutId = setTimeout(() => setHeld(null), 500);
    return () => clearTimeout(timeoutId);
  }, [held]);
  return val !== 0 ? val : (held?.val ?? 0);
};

const Pill = ({children}: {children: ReactNode}) => (
  <Col absolute left="0" right="0" align="center" style={{bottom: 1}}>
    <Box bg="surface" rounded="full" style={{height: 12, paddingLeft: 1, paddingRight: 1}}>
      {children}
    </Box>
  </Col>
);

const GroupSyncBadge = ({connectionKey}: {connectionKey: string}) => {
  const isConnected = useConnectionState((s) => (s[connectionKey]?.connectionCount ?? 0) > 0);
  const lastSynced = useConnectionState((s) => getTime(s[connectionKey]?.lastUpdate ?? null));
  const status = useLaggingStatus(useConnectionState((s) => s[connectionKey]?.status ?? 0));
  if (!isConnected) return <Icon name="no-cloud" size="24" />;

  const getPillContent = () => {
    if (status === 1) {
      return (
        <Pill>
          <Icon name="arrow-down" size="16" className={iconStyles.custom.innerCloudArrow} />
        </Pill>
      );
    }
    if (status === 2) {
      return (
        <Pill>
          <Icon name="arrow-up" size="16" className={iconStyles.custom.innerCloudArrow} />
        </Pill>
      );
    }
    return lastSynced && <Time key={lastSynced} lastSyncedSecs={lastSynced} />;
  };
  return (
    <Box color="secondary" relative>
      <Icon name="cloud" size="24" />
      {getPillContent()}
    </Box>
  );
};

export default GroupSyncBadge;
